// Arc flash labels on the one print card: what's new, what changed, what's current.
// ONE_PRINT_WORDS.md, approved by Robert 2026-10-24 (MOCKUP_ONE_PRINT_REV4.html).
//
// A label is current when what it would print now matches, value for value, what last printed for that
// Asset ID at this site, whatever study printed it. The server keeps one print record per Asset ID
// (afLabelPrints, written by recordLabelSet). Any printed value that moves reads CHANGED, with the move
// spelled out. A new study changes the assessment date on every label, so a new study's set reads CHANGED
// throughout: a new study makes a new set, as Robert set it.

import { DEFAULT_CONFIG, toLabel, type LabelConfig, type LabelKind, type LabelRecord, type LabelValues } from "./af-label-pdf";

export type AfStock = "WARNING" | "DANGER";

/** What printed for one Asset ID, as the server keeps it. */
export interface AfPrintRecord {
  assetId: string;
  stock: AfStock;
  /** The label's printed values and its kind, as JSON (labelFingerprint). */
  values: string;
  at: string;
  studyId: string;
  setId: string;
}

export interface AfCandidate {
  assetId: string;
  stock: AfStock;
  kind: LabelKind;
  /** Null when the row can't print (af_label.py skips it). */
  values: LabelValues | null;
  /** Why it can't print. */
  skip: string[];
  record: LabelRecord;
}

export function stockOf(kind: LabelKind): AfStock {
  return kind === "OVERDUTY" ? "DANGER" : "WARNING";
}

/** One row as the label would print it now. */
export function candidateOf(record: LabelRecord, cfg: LabelConfig = DEFAULT_CONFIG, today = new Date()): AfCandidate {
  const { d, kind, skip } = toLabel(record, cfg, today);
  return { assetId: String(record.Equipment_ID ?? ""), stock: stockOf(kind), kind, values: d, skip, record };
}

/** Every printed value plus the kind, keys in a fixed order, so the same label always gives the same text. */
export function labelFingerprint(kind: LabelKind, v: LabelValues): string {
  const keys = Object.keys(v).sort() as (keyof LabelValues)[];
  return JSON.stringify({ kind, ...Object.fromEntries(keys.map((k) => [k, v[k] ?? ""])) });
}

/** What each printed value is called on the card, in the order they're worth reading. */
const WORDS: [keyof LabelValues | "kind", string][] = [
  ["kind", "Label"],
  ["ie", "Incident energy"],
  ["afb", "Boundary"],
  ["voltage", "Voltage"],
  ["ppe_group", "PPE"],
  ["gloves", "Gloves"],
  ["isolate_at", "Isolate at"],
  ["lab", "Limited approach"],
  ["rab", "Restricted approach"],
  ["wd", "Working distance"],
  ["afc", "Arc flash category"],
  ["aic", "Interrupting rating"],
  ["equip_id", "Equipment"],
  ["date", "Assessed"],
  ["iea_by", "IEA by"],
];

/** "Incident energy 8.4, was 6.1". The first change, then "and 2 more". */
export function changeLine(prev: Record<string, unknown>, now: Record<string, unknown>): string {
  const moved = WORDS.filter(([k]) => String(prev[k] ?? "") !== String(now[k] ?? ""));
  if (!moved.length) return "Changed";
  const [k, w] = moved[0]!;
  const was = String(prev[k] ?? "") || "blank";
  const is = String(now[k] ?? "") || "blank";
  const first = k === "kind" ? `${w} ${stockOf(now[k] as LabelKind)}, was ${stockOf(prev[k] as LabelKind)}` : `${w} ${is}, was ${was}`;
  return moved.length > 1 ? `${first}, and ${moved.length - 1} more` : first;
}

export type AfPrintState = { state: "new" } | { state: "changed"; line: string; at: string } | { state: "current"; at: string };

export function afPrintState(c: AfCandidate, rec: AfPrintRecord | undefined): AfPrintState {
  if (!rec) return { state: "new" };
  if (!c.values) return { state: "changed", line: c.skip[0] ?? "Can't print", at: rec.at };
  const now = labelFingerprint(c.kind, c.values);
  if (now === rec.values) return { state: "current", at: rec.at };
  let prev: Record<string, unknown> = {};
  try {
    prev = JSON.parse(rec.values) as Record<string, unknown>;
  } catch {
    /* an unreadable record reads as all changed */
  }
  return { state: "changed", line: changeLine(prev, JSON.parse(now) as Record<string, unknown>), at: rec.at };
}

/** What recordLabelSet keeps for each label that printed in a set. */
export function printedEntries(printed: readonly AfCandidate[]): { assetId: string; stock: AfStock; values: string }[] {
  return printed.filter((c) => c.values && c.assetId).map((c) => ({ assetId: c.assetId, stock: c.stock, values: labelFingerprint(c.kind, c.values!) }));
}

export { printedOf } from "./af-label-printed";
